import { ref, reactive, computed } from '@vue/composition-api'
import store from '@/store'
import analytics from '@/@core/utils/analytics'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import constants from '@/constants'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { ADD_RETALIER, INVITE_RETAILER, INVITE_RETAILER_FROM_LEADAI } from '@/store/modules/connections.module'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'

const { ROLES } = c
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// --- Field validation helpers (map lookup replaces switch/if-else) ---

function validateEntityName(value) {
  if (!value) return 'Store name is required'
  if (value.length < 3) return 'Must be at least 3 characters'
  return ''
}

function validateRequired(value, label) {
  return value ? '' : `${label} is required`
}

function validateEmail(value) {
  if (!value) return 'Email is required'
  if (!EMAIL_REGEX.test(value)) return 'Invalid email'
  return ''
}

const FIELD_VALIDATORS = {
  entityName: form => validateEntityName(form.entityName),
  firstName: form => validateRequired(form.firstName, 'First name'),
  lastName: form => validateRequired(form.lastName, 'Last name'),
  email: form => validateEmail(form.email),
  country: form => validateRequired(form.country, 'Country'),
}

// --- Form-to-lead field mapping (eliminates repeated optional chaining) ---

const LEAD_FIELD_MAP = [
  ['entityName', 'storeName'],
  ['firstName', 'firstName'],
  ['lastName', 'lastName'],
  ['email', 'email'],
  ['leadId', 'id'],
]

// --- Country matching helpers ---

function isCountryMatch(normalizedInput, normalizedCountry) {
  if (normalizedInput === normalizedCountry) return true
  if (normalizedInput.includes(normalizedCountry)) return true
  if (normalizedCountry.includes(normalizedInput)) return true
  const stripped = normalizedInput.replace(/\bof\b/g, '').replace(/\bthe\b/g, '').replace(/\s+/g, ' ').trim()
  return normalizedCountry.includes(stripped)
}

export function useAddUser() {
  const form = reactive({
    entityName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '',
    country: '',
    leadId: '',
  })

  const formErrors = reactive({
    entityName: '',
    firstName: '',
    lastName: '',
    email: '',
    country: '',
  })

  const isCreating = ref(false)
  const isFormDisabled = ref(false)
  const isFormReadonly = ref(false)
  const fromLeadAI = ref(false)
  const countries = ref([])
  const allRetailers = ref([])
  const allRetailersLocal = ref([])
  const retailers = ref([])

  const brandUser = computed(() => store.getters.profile)

  // Country is required when the country list is available (mirrors the v1
  // modal, where the country field's `required` rule was rendered only inside
  // `v-if="countries.length"`). The guard avoids permanently disabling submit
  // if the country list fails to load.
  const isFormValid = computed(() => form.entityName?.length >= 3
      && !!form.firstName
      && !!form.lastName
      && !!form.email
      && EMAIL_REGEX.test(form.email)
      && (!countries.value.length || !!form.country))

  const selectedRetailerIds = computed(() => retailers.value.map(r => r._id))

  function validateField(field) {
    const validator = FIELD_VALIDATORS[field]
    if (validator) {
      formErrors[field] = validator(form)
    }
    return formErrors[field] || ''
  }

  function getPayloadData() {
    const payload = {
      entityName: form.entityName?.trim(),
      firstName: form.firstName?.trim(),
      lastName: form.lastName?.trim(),
      email: form.email?.trim(),
      country: form.country,
      role: ROLES.RETAILER,
      isSelfManaged: true,
      brandId: brandUser.value?._id,
      isFromLeadAI: fromLeadAI.value,
    }
    if (form.phone) {
      payload.phone = form.phone
      if (form.countryCode) {
        payload.countryCode = form.countryCode
      }
    }
    return payload
  }

  function getRetailersPayload() {
    return retailers.value.map(retailer => retailer._id)
  }

  function resolveSubmitAction(shouldProceed) {
    if (shouldProceed) {
      return {
        actionType: ADD_RETALIER,
        payload: { storeIds: getRetailersPayload(), isFromLeadAI: fromLeadAI.value },
      }
    }
    return {
      actionType: fromLeadAI.value ? INVITE_RETAILER_FROM_LEADAI : INVITE_RETAILER,
      payload: getPayloadData(),
    }
  }

  function handleDuplicateStoresError(err) {
    const stores = err.response?.data?.data?.stores
    allRetailers.value = stores?.sort((a, b) => a.isAdded - b.isAdded) || []
    searchRetailers()
    isFormDisabled.value = true
  }

  function handleSubmitError(err, payload) {
    if (err?.response?.status === 422) {
      handleDuplicateStoresError(err)
      return {
 success: false, is422: true, payload, error: err,
}
    }
    return { success: false, payload, error: err }
  }

  async function submitForm(shouldProceed = false) {
    const { actionType, payload } = resolveSubmitAction(shouldProceed)
    try {
      isCreating.value = true
      await store.dispatch(actionType, { payload })
      apiToastSuccess('Retailer created successfully!', 'CoffeeIcon')
      trackAnalytics()
      return { success: true, payload }
    } catch (err) {
      return handleSubmitError(err, payload)
    } finally {
      isCreating.value = false
    }
  }

  function trackAnalytics() {
    analytics.track(constants.TRACKS.ACTIONS.BRAND_INVITE_RETAILER_USING_WHATSAPP, {
      brandId: brandUser.value?._id,
      retailerId: retailers.value.map(retailer => retailer._id),
      phone: form.phone,
      countryCode: form.countryCode,
      email: form.email,
      fromLeadAI: fromLeadAI.value,
    })
  }

  function handleSelectRetailer(retailer) {
    if (!retailer || retailer.isAdded) return
    if (selectedRetailerIds.value.includes(retailer._id)) {
      retailers.value = retailers.value.filter(r => r._id !== retailer._id)
    } else {
      retailers.value.push(retailer)
    }
  }

  function searchRetailers(searchingText = '') {
    if (!searchingText) {
      allRetailersLocal.value = allRetailers.value
      return
    }
    const query = searchingText.toLowerCase()
    allRetailersLocal.value = allRetailers.value.filter(
      retailer => retailer.entityName.toLowerCase().indexOf(query) !== -1,
    )
  }

  function resetForm() {
    Object.keys(form).forEach(key => { form[key] = '' })
    Object.keys(formErrors).forEach(key => { formErrors[key] = '' })
    isFormDisabled.value = false
    isFormReadonly.value = false
    fromLeadAI.value = false
    retailers.value = []
    allRetailers.value = []
    allRetailersLocal.value = []
  }

  async function loadCountries() {
    try {
      const res = await store.dispatch(GET_COUNTRIES)
      countries.value = res?.data?.data?.COUNTRIES || []
    } catch (err) {
      apiToastError(err)
    }
  }

  function trimField(fieldName) {
    form[fieldName] = (form[fieldName] || '').trim()
  }

  function updateFormData(leadDetails) {
    const details = leadDetails || {}
    LEAD_FIELD_MAP.forEach(([formKey, leadKey]) => {
      form[formKey] = details[leadKey] || ''
    })
  }

  function matchCountry(countryName) {
    if (!countryName) return null
    const normalizedInput = countryName.toLowerCase()
    return countries.value.find(
      country => isCountryMatch(normalizedInput, country.toLowerCase()),
    ) || null
  }

  function getRetailerStatus(retailer) {
    return retailer.isAdded ? '(added)' : ''
  }

  return {
    form,
    formErrors,
    isCreating,
    isFormDisabled,
    isFormReadonly,
    fromLeadAI,
    countries,
    allRetailers,
    allRetailersLocal,
    retailers,
    brandUser,
    isFormValid,
    selectedRetailerIds,
    validateField,
    getPayloadData,
    submitForm,
    handleSelectRetailer,
    searchRetailers,
    resetForm,
    loadCountries,
    trimField,
    updateFormData,
    matchCountry,
    getRetailerStatus,
  }
}
